/**
 * @typedef {import('../types/users').UserData} UserData
 * @typedef {import('../types/certs').GenerateCertResponse} GenerateCertResponse
 * @typedef {import('../types/certs').GetCertStatusResponse} GetCertStatusResponse
 * @typedef {import('../types/certs').EncryptedCSRObject} EncryptedCSRObject
 * @typedef {import('../types/certs').GetCertTypesResponse} GetCertTypesResponse
 * @typedef {import('../types/certs').GetRequirementsResponse} GetRequirementsResponse
 * @typedef {import('../types/certs').CertificateRequestParams} CertificateRequestParams
 * @typedef {import('../types/certs').RequestCertificateResponse} RequestCertificateResponse
 * @typedef {import('../types/certs').ValidateCertificateResponseData} ValidateCertificateResponseData
 * @typedef {import('../types/certs').ValidateCertificateRequestParams} ValidateCertificateRequestParams
 */

import { getConfig } from "../config/index";
import { httpRequest } from "../utils/httpClient";
import helpers from "../utils/helpers";
import { ApacuanaAPIError } from "../errors";
import ApacuanaSuccess from "../success";
import { INTEGRATION_TYPE, PARSE_STATUS } from "../utils/constant";
import { getCustomer } from "./users";

/**
 * @param {EncryptedCSRObject} encryptedCSR
 */
const generateCertOnBoarding = async (encryptedCSR) => {
  try {
    const response = await httpRequest(
      "services/api/register/certificate",
      encryptedCSR,
      "POST"
    );
    const { cert, certifiedid } = response;
    if (!cert || !certifiedid) {
      throw new ApacuanaAPIError(
        "The API response does not contain the certificate.",
        response.status,
        "INVALID_API_RESPONSE"
      );
    }

    return new ApacuanaSuccess({ cert, certifiedid });
  } catch (error) {
    // The captured error is re-thrown, which will already be of type ApacuanaAPIError or a native Error.
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new Error(`Certificate generation failed: ${error.message}`);
  }
};

const generateCertOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Certificate generation is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

/**
 * Generates a digital certificate.
 * @param {EncryptedCSRObject} encryptedCSR - Certificate Signing Request (CSR) object.
 * @returns {Promise<GenerateCertResponse>} Object with the generated certificate and a success indicator.
 * @throws {ApacuanaAPIError} If the CSR is invalid, if the API response does not contain the certificate, or if the integration type is not supported.
 * @throws {Error} If certificate generation fails for another reason.
 */
export const generateCert = async (encryptedCSR) => {
  const { integrationType } = getConfig();
  helpers.validateCsr(encryptedCSR);
  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return generateCertOnBoarding(encryptedCSR);
  }
  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return generateCertOnPremise();
  }

  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};

/**
 * Gets the user's certificate status.
 * @param {boolean} [isCertificateInDevice=false] - Indicates if the certificate is already on the device.
 * @returns {GetCertStatusResponse} Object with the certificate status and a success indicator.
 */
export const getCertStatus = async (isCertificateInDevice = false) => {
  const config = getConfig();
  const customer = await getCustomer();
  const { userData } = customer.data;
  const status = helpers.getCertificateStatus(
    userData,
    isCertificateInDevice,
    config.integrationType
  );
  const parseStatus = PARSE_STATUS[status];
  return new ApacuanaSuccess({ status: parseStatus });
};

const getCertTypesOnBoarding = async () => {
  try {
    const response = await httpRequest(
      "services/api/customer/typeusers",
      {},
      "GET"
    );

    return new ApacuanaSuccess(response);
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new Error(`Failed to get certificate types: ${error.message}`);
  }
};

const getCertTypesOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Getting certificate types is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

/**
 * Gets the available certificate types.
 * @returns {Promise<GetCertTypesResponse>} Object with the list of certificate types and a success indicator.
 * @throws {ApacuanaAPIError} If the API response is invalid or the integration type is not supported.
 * @throws {Error} If the request fails for another reason.
 */
export const getCertTypes = async () => {
  const { integrationType } = getConfig();
  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return getCertTypesOnBoarding();
  }
  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return getCertTypesOnPremise();
  }

  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};

const getRequerimentsByTypeUserOnBoarding = async (type) => {
  try {
    const response = await httpRequest(
      `services/api/customer/documentspref?typeuser=${type}`,
      {},
      "GET"
    );

    return new ApacuanaSuccess(response);
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new Error(`Failed to get requirements: ${error.message}`);
  }
};

const getRequerimentsByTypeUserOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Getting requirements by user type is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

/**
 * Gets the requirements for a given user type.
 * @param {object} params - The parameters.
 * @param {number} params.type - The user type to get requirements for.
 * @returns {Promise<GetRequirementsResponse>} Object with the list of requirements and a success indicator.
 * @throws {ApacuanaAPIError} If the API response is invalid or the integration type is not supported.
 * @throws {Error} If the request fails for another reason or the type is invalid.
 */
export const getRequerimentsByTypeUser = async (params) => {
  if (!params || typeof params.type !== "number") {
    throw new Error(
      'The "params" object with a numeric "type" property is required.'
    );
  }
  const { type } = params;

  const { integrationType } = getConfig();
  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return getRequerimentsByTypeUserOnBoarding(type);
  }
  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return getRequerimentsByTypeUserOnPremise();
  }

  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};

const requestCertificateOnBoarding = async (params) => {
  try {
    const response = await httpRequest(
      "services/api/customer/request-certificate",
      params,
      "POST"
    );

    return new ApacuanaSuccess(response);
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new Error(`Failed to request certificate: ${error.message}`);
  }
};

const requestCertificateOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Requesting a certificate is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

/**
 * Requests a certificate for the user.
 * @param {CertificateRequestParams} params - The parameters for the certificate request.
 * @returns {Promise<RequestCertificateResponse>} Object with a confirmation message and a success indicator.
 * @throws {ApacuanaAPIError} If the API response is invalid or the integration type is not supported.
 * @throws {Error} If the request fails for another reason or the params are invalid.
 */
export const requestCertificate = async (params) => {
  if (
    !params ||
    typeof params.type !== "number" ||
    !Array.isArray(params.documents)
  ) {
    throw new Error(
      'The "params" object with a numeric "type" property and a "documents" array is required.'
    );
  }

  const { integrationType } = getConfig();
  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return requestCertificateOnBoarding(params);
  }
  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return requestCertificateOnPremise();
  }

  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};

// const validateCertificateOnBoarding = async (params) => {
//   throw new ApacuanaAPIError(
//     "Requesting a certificate is not supported for integration type: ONBOARDING",
//     501,
//     "NOT_IMPLEMENTED"
//   );
//   // try {
//   //   const response = await httpRequest(
//   //     "services/api/customer/request-certificate",
//   //     params,
//   //     "POST"
//   //   );
//   //   return new ApacuanaSuccess(response);
//   // } catch (error) {
//   //   if (error instanceof ApacuanaAPIError) {
//   //     throw error;
//   //   }
//   //   throw new Error(`Failed to request certificate: ${error.message}`);
//   // }
// };

// const validateCertificateOnPremise = async () => {
//   throw new ApacuanaAPIError(
//     "Requesting a certificate is not supported for integration type: ONPREMISE",
//     501,
//     "NOT_IMPLEMENTED"
//   );
// };

// /**
//  * Requests a certificate for the user.
//  * @param {ValidateCertificateRequestParams} params - The parameters for the certificate request.
//  * @returns {Promise<ValidateCertificateResponseData>} Object with a confirmation message and a success indicator.
//  * @throws {ApacuanaAPIError} If the API response is invalid or the integration type is not supported.
//  * @throws {Error} If the request fails for another reason or the params are invalid.
//  */
// export const validateCertificate = async (params) => {
//   if (!params || typeof params.challenge !== "string") {
//     throw new Error("Invalid params.");
//   }

//   const { integrationType } = getConfig();
//   if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
//     return requestCertificateOnBoarding(params);
//   }
//   if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
//     return requestCertificateOnPremise();
//   }

//   throw new ApacuanaAPIError(
//     `Unsupported integration type: ${integrationType}`,
//     400,
//     "UNSUPPORTED_INTEGRATION_TYPE"
//   );
// };
